import { useTranslation } from 'react-i18next';
import { History, AlertCircle } from 'lucide-react';
import { trpc } from '@/lib/api/trpc';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DateDisplay } from '@/components/date-display';

interface FunnelVersionHistoryDialogProps {
  funnelId: string;
  environment?: 'staging' | 'production';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface VersionTimelineEntry {
  version: number;
  environment: 'staging' | 'production';
  createdAt: Date | string;
  createdBy?: string;
  changeType: 'create' | 'update' | 'promote' | 'demote' | 'rollback' | 'migration';
  changeDescription: string;
  configHash: string;
}

export function FunnelVersionHistoryDialog({
  funnelId,
  environment,
  open,
  onOpenChange,
}: FunnelVersionHistoryDialogProps) {
  const { t } = useTranslation('storefront');
  const { data, isLoading, error } = trpc.funnels.getVersionHistory.useQuery(
    {
      funnelId,
      environment,
      limit: 50,
    },
    { enabled: open },
  );

  const versions = data?.versions || [];

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'create':
        return 'default';
      case 'update':
        return 'secondary';
      case 'promote':
        return 'default';
      case 'demote':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getChangeTypeLabel = (changeType: string) => {
    switch (changeType) {
      case 'create':
        return t('versionHistory.changeType.create');
      case 'update':
        return t('versionHistory.changeType.update');
      case 'promote':
        return t('versionHistory.changeType.promote');
      case 'demote':
        return t('versionHistory.changeType.demote');
      case 'rollback':
        return t('versionHistory.changeType.rollback');
      case 'migration':
        return t('versionHistory.changeType.migration');
      default:
        return t('versionHistory.changeType.changed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            {t('versionHistory.title')}
          </DialogTitle>
          <DialogDescription>{t('versionHistory.description')}</DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="space-y-2 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
              <p className="text-sm text-slate-500">{t('versionHistory.loading')}</p>
            </div>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {!isLoading && !error && versions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <History className="mb-4 h-12 w-12 text-slate-300" />
            <h3 className="mb-2 text-lg font-medium text-slate-900 dark:text-slate-100">
              {t('versionHistory.empty')}
            </h3>
            <p className="text-sm text-slate-500">
              {t('versionHistory.emptyDescription')}
            </p>
          </div>
        )}

        {!isLoading && !error && versions.length > 0 && (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('versionHistory.analyticsNote')}
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              {versions.map((version: VersionTimelineEntry, index: number) => (
                <div
                  key={`${version.version}-${version.configHash}`}
                  className="relative border-l-2 border-slate-200 pb-6 pl-8 last:border-l-0 last:pb-0 dark:border-slate-700"
                >
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-0 -ml-[9px] flex h-4 w-4 items-center justify-center rounded-full border-2 border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                    <div
                      className={`h-2 w-2 rounded-full ${
                        index === 0 ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {t('versionHistory.version', { number: version.version })}
                          </span>
                          <Badge variant={getChangeTypeColor(version.changeType)}>
                            {getChangeTypeLabel(version.changeType)}
                          </Badge>
                          {version.environment && (
                            <Badge variant={version.environment === 'production' ? 'default' : 'secondary'}>
                              {version.environment}
                            </Badge>
                          )}
                          {index === 0 && <Badge variant="outline">{t('versionHistory.current')}</Badge>}
                        </div>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                          {version.changeDescription}
                        </p>
                        {version.createdBy && (
                          <p className="mt-1 text-xs text-slate-500">{t('versionHistory.by', { user: version.createdBy })}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-slate-500">
                          <DateDisplay value={version.createdAt} format="MMM d, yyyy" />
                        </p>
                        <p className="text-xs text-slate-400">
                          <DateDisplay value={version.createdAt} format="h:mm a" />
                        </p>
                      </div>
                    </div>

                    <div className="break-all font-mono text-xs text-slate-400">
                      Hash: {version.configHash.substring(0, 12)}...
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {versions.length >= 50 && (
              <p className="pt-4 text-center text-xs text-slate-500">{t('versionHistory.showingLast', { count: 50 })}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
